import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'

import { BackContinue } from '@components/back_continue/back_continue'
import { ContactInformation } from '@components/profile_form/form'
import { saveSMSConsent } from '@components/profile_form/save_sms_consent'
import { profileSchema, setupInitialValues, TProfileSchema } from '@components/profile_form/schema'
import { i18WithParams as t } from '@shared/locale'
import { shallowEqual } from 'components-ui/src/shared/common'
import { buildErrorMessage } from 'components-ui/src/shared/error'
import { CustomError } from 'components-ui/src/shared/error_handler'
import { useClientLocationUserProfile } from 'service-api/src/hooks/use_client_location_user_profiles'
import { ClientLocationUserProfileService } from 'service-api/src/services/client_location_user_profiles.service'
import { captureException } from 'service-api/src/shared/monitoring'
import type { LocationUserProfileClientDto, LocationUserProfileUpdateInput } from 'service-api/src/types/api'

interface TWaitlistContactFormProps {
  locationName: string
  facilityName: string
  lupId: string
  onBack: () => void
  onConfirm: () => Promise<boolean>
}

const profileUpdate = (values: TProfileSchema): LocationUserProfileUpdateInput => ({
  displayName: `${values.firstName} ${values.lastName}`,
  firstName: values.firstName,
  lastName: values.lastName,
  phone: values.phone,
  streetAddress: values.streetAddress,
  streetAddress2: values.streetAddress2?.trim() ? values.streetAddress2 : null,
  city: values.city,
  state: values.state,
  zipCode: values.zipCode,
})

export const WaitlistContactForm = (props: TWaitlistContactFormProps) => {
  const profile = useClientLocationUserProfile(props.locationName, props.lupId)
  const [initialProfile, setInitialProfile] = useState<LocationUserProfileClientDto>()

  useEffect(() => {
    // Wait for the current profile, including a refresh of cached data. Once the
    // draft is mounted, background refetches must not overwrite the user's edits.
    if (!initialProfile && profile.isSuccess && !profile.isFetching && profile.data) {
      setInitialProfile(profile.data)
    }
  }, [initialProfile, profile.isSuccess, profile.isFetching, profile.data])

  if (props.lupId && initialProfile) {
    return <LoadedWaitlistContactForm {...props} profile={initialProfile} />
  }

  return (
    <>
      {profile.isError || (profile.isSuccess && !profile.data) ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" disabled={profile.isFetching} onClick={() => profile.refetch()}>
              {t('global.notFound.tryAgain')}
            </Button>
          }
        >
          {t('waitlist.profileLoadError')}
        </Alert>
      ) : (
        <Skeleton variant="rectangular" height={320} aria-label={t('waitlist.profileLoading')} />
      )}
      <BackContinue
        onBack={props.onBack}
        onContinue={async () => false}
        continueEnabled={false}
        continueLabel={t('waitlist.actionButton')}
      />
    </>
  )
}

const LoadedWaitlistContactForm = ({
  locationName,
  facilityName,
  lupId,
  profile,
  onBack,
  onConfirm,
}: TWaitlistContactFormProps & { profile: LocationUserProfileClientDto }) => {
  const [confirmedConsent, setConfirmedConsent] = useState(profile.smsConsentStatus === 'ACTIVE')
  const savedProfile = useRef(profileUpdate(setupInitialValues(profile)))
  const submitting = useRef(false)
  const formik = useFormik<TProfileSchema>({
    initialValues: setupInitialValues(profile),
    validationSchema: profileSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      const updatedProfile = profileUpdate(values)
      try {
        if (!shallowEqual(savedProfile.current, updatedProfile)) {
          await ClientLocationUserProfileService.put(locationName, lupId, updatedProfile)
          // A failed waitlist request must not cause an already-saved profile to
          // be submitted again on retry.
          savedProfile.current = updatedProfile
        }
      } catch (error) {
        const message = buildErrorMessage(error)
        enqueueSnackbar(message, { variant: 'error' })
        captureException(new CustomError(message, 'Waitlist.updateProfile', { cause: error }))
        return
      }

      if (values.consentToSMS && !confirmedConsent) {
        const confirmed = await saveSMSConsent(locationName, {
          lupId,
          operatorEmail: values.email,
          phoneNumber: values.phone,
        })
        if (confirmed) setConfirmedConsent(true)
      }

      await onConfirm()
    },
  })

  const submit = async (): Promise<boolean> => {
    if (submitting.current) return false
    submitting.current = true
    try {
      // Formik validates here too, including submissions through Enter.
      await formik.submitForm()
    } finally {
      submitting.current = false
    }
    // Waitlist owns its confirmation screen; suppress BackContinue navigation.
    return false
  }

  return (
    <Box
      component="form"
      aria-label={t('detailsPolicies.contactInformation')}
      noValidate
      onBlur={(event) => {
        const field = event.target.getAttribute('name') || event.target.id
        if (field && field in formik.values) formik.handleBlur(event)
      }}
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <Box component="fieldset" disabled={formik.isSubmitting} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
        <ContactInformation
          formik={formik}
          onChange={formik.handleChange}
          onChangeSelect={(_event, state) => formik.setFieldValue('state', state?.code ?? '')}
          size="small"
          smsConsent={{
            checked: formik.values.consentToSMS,
            confirmed: confirmedConsent,
            isSaving: formik.isSubmitting,
            locationName: facilityName,
            onChange: (checked) => formik.setFieldValue('consentToSMS', checked),
          }}
        />
      </Box>
      {!formik.isValid && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('waitlist.contactRequired')}
        </Alert>
      )}
      <BackContinue
        onBack={onBack}
        onContinue={submit}
        continueEnabled={formik.isValid && !formik.isValidating && !formik.isSubmitting}
        continueLabel={t('waitlist.actionButton')}
      />
    </Box>
  )
}
